import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FilterBar, DESTINATION_OPTIONS, TYPE_OPTIONS } from '../../../components/shared/FilterBar';
import { PageToggleButton } from '../../../components/shared/PageToggleButton/PageToggleButton';
import { HandDrawnButton } from '../../../components/shared/HandDrawnButton/HandDrawnButton';
import { CTALink } from '../../../components/shared/CTALink/CTALink';
import './ArticleGallery.css';

interface ArtImmersion {
  id: string;
  title: string;
  location: string;
  date: string;
  description: string;
  image: string;
  hasVideo?: boolean;
}

interface ArticleGalleryProps {
  immersions: ArtImmersion[];
  itemsPerPage?: number;
}

const ArticleGallery: React.FC<ArticleGalleryProps> = ({ 
  immersions, 
  itemsPerPage = 4
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDestination, setFilterDestination] = useState<string | undefined>(undefined);
  const [filterType, setFilterType] = useState<string | undefined>(undefined);
  
  const totalPages = Math.ceil(immersions.length / itemsPerPage);
  
  return (
    <section className="article-gallery">
      {/* Filter Section - Outside of containers */}
      <FilterBar
        className="art-immersions-filters"
        dropdowns={[
          {
            id: 'destination',
            type: 'destination',
            level: 'brand',
            options: DESTINATION_OPTIONS,
            selectedValue: filterDestination,
            onSelect: (value: string) => setFilterDestination(value === filterDestination ? undefined : value)
          },
          {
            id: 'type',
            type: 'type',
            level: 'brand',
            options: TYPE_OPTIONS,
            selectedValue: filterType,
            onSelect: (value: string) => setFilterType(value === filterType ? undefined : value)
          }
        ]}
        alignment="left"
      />

      {/* Art Cards Grid */}
      <div className="art-cards-grid">
          {immersions.map((immersion) => (
            <article key={immersion.id} className="art-card">
              <div className="card-image">
                <img src={immersion.image} alt={immersion.title} />
              </div>
              <div className="card-content">
                <h3>{immersion.title}</h3>
                <div className="card-meta">
                  <span className="location">{immersion.location}</span>
                  <span className="date">{immersion.date}</span>
                </div>
                <p className="card-description">
                  {immersion.description}
                </p>
                <div className="card-actions">
                  <CTALink
                    text={t('pages/artImmersions:common.readMore')}
                    onClick={() => navigate(`/art-immersions/${immersion.id}`)}
                    type="secondary"
                    color="awasi"
                    className="read-more"
                  />
                  {immersion.hasVideo && (
                    <HandDrawnButton
                      text={t('pages/artImmersions:common.watchVideo')}
                      onClick={() => navigate(`/art-immersions/${immersion.id}#video`)}
                      variant="brand"
                      color="off-white"
                      animation="stroke-to-stroke"
                      className="watch-video"
                    />
                  )}
                </div>
              </div>
            </article>
          ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <PageToggleButton
                key={page}
                number={page}
                isActive={currentPage === page}
                onClick={() => setCurrentPage(page)}
                color="awasi"
                className="art-gallery-pagination-button"
              />
            ))}
        </div>
      )}
    </section>
  );
};

export default ArticleGallery;